// src/features/views/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  CheckboxField,
  InputField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// static identifiers
const client = "tableau-quantilia";
const table_name = "views";

// Zod schema for create‐new form
const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  sort_order: z.coerce.number().optional().nullable(),
  content_url: z.string().min(1, "Content URL is required"),
  hidden_from_tab_bar: z.boolean().optional().nullable(),
  // AutocompleteAsyncField for workbook lookup (both display and ID)
  workbook_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  workbook_id: z.coerce.number().optional().nullable(),
});

export function ViewNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  const DEFAULTS = {
    name: "",
    sort_order: null,
    content_url: "",
    hidden_from_tab_bar: false,
    group_user_name: null,
    user_group_id: null,
  };

  const onResetNew = () => {
    form.reset(DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
      keepValues: false,
    });
    handleReset();
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New View
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Name & Sort Order */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="workbook_name"
                    label={t(`${table_name}.columns.workbook_name`, {
                      ns: "tables",
                      defaultValue: "Workbook",
                    })}
                    base_url="tableau-quantilia/workbooks"
                    column_name="name"
                    id_column="workbook_id"
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Row 2: Content URL & Workbook selector */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="content_url"
                    label={t(`${table_name}.columns.content_url`, {
                      ns: "tables",
                      defaultValue: "Content URL",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="sort_order"
                    label={t(`${table_name}.columns.sort_order`, {
                      ns: "tables",
                      defaultValue: "Sort Order",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="hidden_from_tab_bar"
                    label={t(`${table_name}.columns.hidden_from_tab_bar`, {
                      ns: "tables",
                      defaultValue: "Hidden from tab bar",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={onResetNew}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirm New‐item Modal */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, ["workbook_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (leave) Confirmation Modal */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
